
import React from "react";
import dynamic from "next/dynamic";
import { MapPin, Phone, Clock } from "lucide-react";
import useSiteSettings, { splitPhones } from "utils/useSiteSettings";
import SectionHeading from "components/SectionHeading";

const MapWithNoSSR = dynamic(() => import("./MapComponent"), { ssr: false });

// Салбаруудыг админ панелаас (Салбар) бүртгэнэ. Бүртгээгүй үед бусад компанийн хаяг харуулахгүйн тулд хоосон.
const fallbackBranches = [];

// HTML тэгийг цэвэрлэх (хаяг нь TextEditor-оор HTML агуулж болзошгүй)
const stripHtml = (s = "") => String(s).replace(/<[^>]*>/g, "").trim();

const normalize = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((b) => ({
      name: b.name || "",
      address: stripHtml(b.address),
      phone: b.phone || "",
      hours: b.hours || "",
      lat: b.lat != null && b.lat !== "" ? parseFloat(b.lat) : null,
      lng: b.lng != null && b.lng !== "" ? parseFloat(b.lng) : null,
      position: b.position || 0,
    }))
    .filter((b) => b.name || b.address)
    .sort((a, b) => (a.position || 0) - (b.position || 0));

const BranchLocations = ({ branches = [], location = [] }) => {
  const { get } = useSiteSettings();

  // Эрэмбэ: (1) branch хүснэгт → (2) хуучин location → (3) fallback
  const fromBranch = normalize(branches);
  const fromLocation = normalize(location);
  const list = fromBranch.length ? fromBranch : fromLocation.length ? fromLocation : fallbackBranches;

  // Газрын зураг дээр зөвхөн координаттай салбаруудыг тэмдэглэнэ
  const mapped = list.filter((b) => b.lat != null && b.lng != null && !isNaN(b.lat) && !isNaN(b.lng));

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <SectionHeading eyebrow="Салбарууд" title={get("branch.title")} subtitle={get("branch.subtitle")} />

        {/* Зүүн: газрын зураг · Баруун: салбарын жагсаалт (гар утсанд жагсаалт эхэлнэ) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-3 lg:max-h-[560px] lg:overflow-y-auto lg:pr-2">
            {!list.length && (
              <div className="rounded-2xl bg-slate-50 border border-dashed border-border p-5 text-sm text-muted-foreground">
                Салбарын мэдээлэл удахгүй нэмэгдэнэ.
              </div>
            )}
            {list.map((branch, index) => (
              <div
                key={index}
                className="rounded-2xl bg-slate-50 border border-transparent p-5 hover:bg-white hover:border-primary/40 hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <MapPin className="w-[18px] h-[18px]" />
                  </span>
                  <h3 className="flex-1 font-display font-bold text-foreground">{branch.name || `Салбар ${index + 1}`}</h3>
                  <span className="text-xs font-bold text-muted-foreground/60 tabular-nums">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                {branch.address && (
                  <p className="mt-3 pl-12 text-sm text-muted-foreground">{branch.address}</p>
                )}
                {branch.phone && (
                  <p className="mt-2 pl-12 flex items-center gap-2 flex-wrap text-sm">
                    <Phone className="w-4 h-4 text-primary shrink-0" />
                    {splitPhones(branch.phone).map((p) => (
                      <a key={p} href={`tel:${p}`} className="font-semibold text-brand-blue hover:text-primary transition-colors">
                        {p}
                      </a>
                    ))}
                  </p>
                )}
                {branch.hours && (
                  <p className="mt-2 pl-12 flex items-start gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                    <span>{branch.hours}</span>
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="lg:col-span-7 lg:order-first h-[420px] lg:h-[560px] rounded-3xl overflow-hidden border border-border shadow-lg bg-white">
            <MapWithNoSSR branches={mapped} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BranchLocations;
